import React, { useEffect, useRef } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

import i18n from 'i18n-js';

import {
    formatGuestName,
    formatReservationDate,
    formValuesToModel,
    getDatePart,
    ReservationFormValues,
    reservationToFormValues,
    validateReservationForm,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import { ToasterType } from 'store/globalStore/actionCreators';
import reservationsStore from 'store/reservationsStore';
import ReservationEditor from './components/ReservationEditor';
import { FormSkeleton } from 'components/shared';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
    RESERVATIONS_PATH,
    useReservationsRestaurantId,
    useReservationsSubpageGuard,
} from './hooks';
import { ACTIVE_TAB_STATE } from './index';
import { ProcessRequestLocationState } from './components/RequestActions';
import { ReservationDTO } from 'api/controllers/reservations';

export default function ProcessRequestPage() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const location = useLocation<ProcessRequestLocationState | undefined>();
    const activeFilter = location.state?.activeFilter;
    const dispatch = useThunkDispatch();
    const isModuleDisabled = useReservationsSubpageGuard();
    const requests = useAppSelector((state) => state.reservations.requests);
    const isRequestsLoaded = useAppSelector(
        (state) => state.reservations.requestsRestaurantId === state.user.currentRestaurantId
    );
    const restaurantId = useReservationsRestaurantId();
    const isInformationSettled = useAppSelector(
        (state) => state.restaurantDetails.isRestaurantInformationSettled
    );
    const storedRequest = requests.find((item) => item.id === id);
    const requestRef = useRef(storedRequest ?? location.state?.reservation);
    if (storedRequest) {
        requestRef.current = storedRequest;
    }
    const request = requestRef.current;

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.selectRequest(id));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const showToaster = (type: ToasterType, message: unknown) =>
        dispatch(globalStore.actionCreators.showToaster(type, message));

    const backToRequests = (reservation?: ReservationDTO) =>
        history.replace(
            RESERVATIONS_PATH,
            activeFilter
                ? { ...ACTIVE_TAB_STATE, activeFilter, reservation: reservation ?? request }
                : undefined
        );

    const handleSave = async (values: ReservationFormValues) => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.saveRequest(id, formValuesToModel(values))
            );
            if (result.status === 'saved') {
                showToaster('success', i18n.t('messages.updatedSuccesfully'));
            } else {
                showToaster('error', i18n.t('reservations.alreadyHandled'));
            }
            backToRequests(result.status === 'saved' ? result.reservation : undefined);
        } catch (error) {
            showToaster('error', error);
        }
    };

    const handleConfirm = async (values: ReservationFormValues): Promise<string | null> => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.confirmRequest(id, formValuesToModel(values))
            );
            if (result.status === 'tableTaken') {
                return result.tableId;
            }
            if (result.status === 'alreadyHandled') {
                showToaster('error', i18n.t('reservations.alreadyHandled'));
                backToRequests();
                return null;
            }
            const { email, reservationStartDateTime } = result.reservation;
            showToaster(
                'success',
                email
                    ? i18n.t('reservations.confirmedEmailSent', { email })
                    : i18n.t('reservations.confirmedNoEmail')
            );
            history.replace(RESERVATIONS_PATH, {
                ...ACTIVE_TAB_STATE,
                date: getDatePart(reservationStartDateTime),
                activeFilter: activeFilter
                    ? { ...activeFilter, day: getDatePart(reservationStartDateTime) }
                    : undefined,
            });
        } catch (error) {
            showToaster('error', error);
        }
        return null;
    };

    if (isModuleDisabled) {
        return <Redirect to={RESERVATIONS_PATH} />;
    }
    if (!request && isRequestsLoaded) {
        return <Redirect to={RESERVATIONS_PATH} />;
    }

    return (
        <div style={{ position: 'relative', height: '100%' }}>
            {request && (
                <ReservationEditor
                    title={i18n
                        .t('reservations.processRequestTitle', { name: formatGuestName(request) })
                        .toUpperCase()}
                    channelLine={i18n.t('reservations.channelReceived', {
                        channel: i18n.t(`reservations.channels.${request.channel}`),
                        dateTime: formatReservationDate(request.createDate, 'dd.MM.yyyy, HH:mm'),
                    })}
                    reservationId={request.id}
                    initialValues={reservationToFormValues(request)}
                    validate={validateReservationForm}
                    confirmLabel={i18n.t('reservations.confirmReservation')}
                    onCancel={() => backToRequests()}
                    onSave={handleSave}
                    onConfirm={handleConfirm}
                />
            )}
            <FormSkeleton
                isLoading={!request && !(isInformationSettled && !restaurantId)}
                title
                columns={[]}
                aside={6}
                actions={2}
            />
        </div>
    );
}
